import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { 
  ArrowRight, 
  Check,
  TrendingUp,
  Users,
  Zap,
  Target,
  BarChart3,
  Smartphone,
  Globe,
  Bot,
  MessageCircle,
  Mail,
  MapPin,
  Instagram,
  ChevronRight,
  Play,
  Sparkles,
  X,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PaymentModal from '../components/PaymentModal';

const API = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ai-websites-2/artifacts/idq8yn2y_WhatsApp%20Image%202026-03-09%20at%2009.42.59.jpeg";

// Animation variants
const fadeIn = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.25, 0.1, 0.25, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.1 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

// Animated Section Wrapper
function AnimatedSection({ children, className = "", id }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  
  return (
    <motion.section
      id={id}
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.section>
  );
}

// Navigation
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  
  React.useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/90 backdrop-blur-xl border-b border-white/5' : 'bg-transparent'
    }`}>
      <div className="max-w-6xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <a href="/" className="flex items-center" data-testid="logo">
            <img src={LOGO_URL} alt="AG Collective" className="h-12 w-auto invert" />
          </a>
          
          <div className="hidden md:flex items-center gap-8">
            {['servicios', 'resultados', 'portafolio', 'precios', 'contacto'].map((item) => (
              <button 
                key={item}
                onClick={() => scrollToSection(item)} 
                className="text-sm text-neutral-400 hover:text-white transition-colors capitalize"
              >
                {item}
              </button>
            ))}
          </div>
          
          <Button 
            onClick={() => scrollToSection('contacto')}
            className="bg-white text-black hover:bg-neutral-200 rounded-full px-5 py-2 text-sm font-medium transition-all"
            data-testid="nav-cta"
          >
            Agendar llamada
          </Button>
        </div>
      </div>
    </nav>
  );
}

// Hero Section
function HeroSection() {
  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-hidden pt-16">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-neutral-900 via-black to-black" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-gradient-to-b from-white/[0.03] to-transparent rounded-full blur-3xl" />
      </div>
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:60px_60px]" />
      
      <div className="max-w-6xl mx-auto px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="text-center"
        >
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 mb-8"
          >
            <Sparkles className="w-4 h-4 text-neutral-400" />
            <span className="text-sm text-neutral-400">Agencia de Marketing Digital en México</span>
          </motion.div>
          
          {/* Headline */}
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.1] mb-6 max-w-4xl mx-auto"
            data-testid="hero-headline"
          >
            Escalamos negocios en México con marketing digital y{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-neutral-200 to-neutral-500">
              automatización con IA
            </span>
          </motion.h1>
          
          {/* Subheadline */}
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="text-lg md:text-xl text-neutral-400 max-w-2xl mx-auto mb-10 leading-relaxed"
          >
            Ayudamos a empresas a atraer más clientes, automatizar procesos y crecer con estrategias digitales modernas.
          </motion.p>
          
          {/* CTAs */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <Button 
              onClick={() => document.getElementById('contacto')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-white text-black hover:bg-neutral-200 rounded-full px-8 py-6 text-base font-medium transition-all group"
              data-testid="hero-primary-cta"
            >
              Agendar demo gratuito
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
            <Button 
              onClick={() => document.getElementById('servicios')?.scrollIntoView({ behavior: 'smooth' })}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10 rounded-full px-8 py-6 text-base font-medium transition-all"
              data-testid="hero-secondary-cta"
            >
              Ver servicios
            </Button>
          </motion.div>
          
          {/* Trust indicators */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap justify-center gap-8 mt-16 pt-16 border-t border-white/5"
          >
            {[
              { value: "+50", label: "Clientes activos" },
              { value: "+$2M", label: "Generados en ventas" },
              { value: "4.9★", label: "Calificación promedio" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <p className="text-2xl font-semibold text-white">{stat.value}</p>
                <p className="text-sm text-neutral-500">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// Problem Section
function ProblemSection() {
  const problems = [
    { icon: Smartphone, text: "Redes sociales sin estrategia" },
    { icon: Target, text: "Publicidad que no convierte" },
    { icon: Bot, text: "Falta de automatización" },
    { icon: Users, text: "Poca generación de leads" }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-black">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-6">
            Muchos negocios tienen presencia digital,<br />
            <span className="text-neutral-500">pero no resultados.</span>
          </h2>
        </motion.div>
        
        <motion.div variants={fadeIn} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
          {problems.map((problem, i) => (
            <div 
              key={i} 
              className="p-6 rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-white/10 transition-colors"
            >
              <problem.icon className="w-8 h-8 text-neutral-500 mb-4" />
              <p className="text-neutral-300">{problem.text}</p>
            </div>
          ))}
        </motion.div>
        
        <motion.div variants={fadeIn} className="text-center">
          <p className="text-xl text-neutral-400">
            Ahí es donde entra{' '}
            <span className="text-white font-medium">AG Collective.</span>
          </p>
        </motion.div>
      </div>
    </AnimatedSection>
  );
}

// Services Section
function ServicesSection() {
  const services = [
    {
      icon: Users,
      title: "Social Media Management",
      description: "Creación de contenido, estrategia y crecimiento orgánico para tus redes sociales."
    },
    {
      icon: Target,
      title: "Paid Advertising",
      description: "Campañas en Meta Ads y Google Ads enfocadas en generación de leads."
    },
    {
      icon: Globe,
      title: "Website & Landing Pages",
      description: "Diseño de páginas optimizadas para conversión y generación de clientes."
    },
    {
      icon: Bot,
      title: "AI Automation",
      description: "Automatización de procesos, chatbots y funnels inteligentes."
    }
  ];

  return (
    <AnimatedSection id="servicios" className="py-24 md:py-32 bg-neutral-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Servicios</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Nuestros servicios
          </h2>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((service, i) => (
            <motion.div
              key={i}
              variants={fadeIn}
              className="group p-8 rounded-2xl bg-neutral-900/30 border border-white/5 hover:border-white/10 hover:bg-neutral-900/50 transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-white/5 flex items-center justify-center mb-6 group-hover:bg-white/10 transition-colors">
                <service.icon className="w-6 h-6 text-neutral-400" />
              </div>
              <h3 className="text-xl font-medium mb-3 text-white">{service.title}</h3>
              <p className="text-neutral-400 leading-relaxed">{service.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

// Results Section
function ResultsSection() {
  const metrics = [
    { value: "+230%", label: "Crecimiento en engagement", icon: TrendingUp },
    { value: "+150", label: "Leads en 30 días", icon: Users },
    { value: "5x", label: "Retorno en campañas", icon: BarChart3 }
  ];

  return (
    <AnimatedSection id="resultados" className="py-24 md:py-32 bg-black">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Resultados</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Resultados reales
          </h2>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {metrics.map((metric, i) => (
            <motion.div
              key={i}
              variants={scaleIn}
              className="text-center p-10 rounded-2xl bg-gradient-to-b from-neutral-900/50 to-transparent border border-white/5"
            >
              <metric.icon className="w-8 h-8 text-neutral-500 mx-auto mb-6" />
              <p className="text-5xl md:text-6xl font-semibold text-white mb-3">{metric.value}</p>
              <p className="text-neutral-400">{metric.label}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

// Portfolio Section
function PortfolioSection() {
  const projects = [
    {
      name: "Mezcal Sacrvm",
      url: "https://mezcalsacrvm.com/",
      category: "E-commerce",
      description: "Tienda online de mezcal artesanal",
      gradient: "from-amber-900/20 to-orange-900/20"
    },
    {
      name: "Newlux",
      url: "https://newlux.com.mx/",
      category: "Corporativo",
      description: "Empresa de iluminación LED",
      gradient: "from-blue-900/20 to-cyan-900/20"
    },
    {
      name: "Panther Media",
      url: "https://panthermedia.mx/",
      category: "Agencia",
      description: "Agencia de marketing digital",
      gradient: "from-purple-900/20 to-pink-900/20"
    },
    {
      name: "Solarza",
      url: "https://www.solarza.mx/",
      category: "Energía",
      description: "Soluciones de energía solar",
      gradient: "from-green-900/20 to-emerald-900/20"
    }
  ];

  return (
    <AnimatedSection id="portafolio" className="py-24 md:py-32 bg-neutral-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Portafolio</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-4">
            Proyectos recientes
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Conoce algunos de los sitios web que hemos diseñado para nuestros clientes.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {projects.map((project, i) => (
            <motion.a
              key={i}
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              variants={fadeIn}
              className="group relative overflow-hidden rounded-2xl bg-neutral-900/50 border border-white/5 hover:border-white/20 transition-all duration-300"
              data-testid={`portfolio-${i}`}
            >
              {/* Gradient Background with Website Preview */}
              <div className={`aspect-video relative overflow-hidden bg-gradient-to-br ${project.gradient}`}>
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-[90%] h-[85%] rounded-lg overflow-hidden shadow-2xl border border-white/10 bg-neutral-900">
                    <div className="h-6 bg-neutral-800 flex items-center px-3 gap-1.5">
                      <div className="w-2.5 h-2.5 rounded-full bg-red-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/70" />
                      <div className="w-2.5 h-2.5 rounded-full bg-green-500/70" />
                      <div className="flex-1 mx-3">
                        <div className="h-3 bg-neutral-700 rounded text-[8px] text-neutral-400 flex items-center justify-center">
                          {project.url.replace('https://', '').replace('www.', '')}
                        </div>
                      </div>
                    </div>
                    <img 
                      src={`https://image.thum.io/get/width/600/crop/800/${project.url}`}
                      alt={project.name}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </div>
              
              {/* Content */}
              <div className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-neutral-500 uppercase tracking-wider">{project.category}</span>
                  <ExternalLink className="w-4 h-4 text-neutral-500 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-xl font-medium text-white mb-1 group-hover:text-neutral-200 transition-colors">
                  {project.name}
                </h3>
                <p className="text-sm text-neutral-500">{project.description}</p>
              </div>
            </motion.a>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

// Process Section
function ProcessSection() {
  const steps = [
    { number: "01", title: "Diagnóstico estratégico", description: "Analizamos tu negocio, competencia y oportunidades de crecimiento." },
    { number: "02", title: "Diseño de estrategia", description: "Creamos un plan personalizado para alcanzar tus objetivos." },
    { number: "03", title: "Implementación", description: "Ejecutamos la estrategia con precisión y seguimiento constante." },
    { number: "04", title: "Optimización y escalamiento", description: "Mejoramos continuamente y escalamos lo que funciona." }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-neutral-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Proceso</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Nuestro proceso
          </h2>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {steps.map((step, i) => (
            <motion.div
              key={i}
              variants={fadeIn}
              className="relative p-6"
            >
              <span className="text-6xl font-bold text-neutral-900 absolute -top-2 left-4">{step.number}</span>
              <div className="relative z-10 pt-8">
                <h3 className="text-lg font-medium mb-2 text-white">{step.title}</h3>
                <p className="text-sm text-neutral-500 leading-relaxed">{step.description}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

// Pricing Section
function PricingSection({ onSelectPlan }) {
  const plans = [
    {
      id: "start",
      name: "START",
      price: "$8,000",
      period: " MXN / año",
      description: "Para negocios que inician en digital",
      features: [
        "Página web de 1-3 secciones",
        "Diseño moderno y profesional",
        "Botón de WhatsApp",
        "Optimización móvil",
        "Formulario de contacto"
      ],
      highlighted: false
    },
    {
      id: "pro",
      name: "PRO",
      price: "$12,000",
      period: " MXN / año",
      description: "Para negocios en crecimiento",
      features: [
        "Página web de 5 secciones",
        "Optimización SEO básica",
        "Integración Google Maps",
        "Integración WhatsApp",
        "Galería de servicios"
      ],
      highlighted: true
    },
    {
      id: "premium",
      name: "PREMIUM",
      price: "$16,000",
      period: " MXN / año",
      description: "Para negocios que quieren escalar",
      features: [
        "Página web completa",
        "SEO local optimizado",
        "Copywriting optimizado",
        "Blog integrado",
        "Optimización de velocidad"
      ],
      highlighted: false
    }
  ];

  return (
    <AnimatedSection id="precios" className="py-24 md:py-32 bg-black">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Planes</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-4">
            Planes de crecimiento
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Elige el plan que mejor se adapte a las necesidades de tu negocio.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, i) => (
            <motion.div
              key={plan.id}
              variants={fadeIn}
              className={`relative p-8 rounded-2xl transition-all duration-300 ${
                plan.highlighted 
                  ? 'bg-white text-black' 
                  : 'bg-neutral-900/50 border border-white/5 hover:border-white/10'
              }`}
              data-testid={`pricing-card-${plan.id}`}
            >
              {plan.highlighted && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-black text-white text-xs font-medium rounded-full">
                  Más popular
                </div>
              )}
              
              <div className="mb-8">
                <p className={`text-sm font-medium tracking-wider mb-4 ${plan.highlighted ? 'text-neutral-600' : 'text-neutral-500'}`}>
                  {plan.name}
                </p>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="text-4xl font-semibold">{plan.price}</span>
                  <span className={`text-sm ${plan.highlighted ? 'text-neutral-600' : 'text-neutral-500'}`}>{plan.period}</span>
                </div>
                <p className={`text-sm ${plan.highlighted ? 'text-neutral-600' : 'text-neutral-500'}`}>
                  {plan.description}
                </p>
              </div>
              
              <ul className="space-y-3 mb-8">
                {plan.features.map((feature, j) => (
                  <li key={j} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 ${plan.highlighted ? 'text-black' : 'text-neutral-500'}`} />
                    <span className={`text-sm ${plan.highlighted ? 'text-neutral-700' : 'text-neutral-400'}`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>
              
              <Button
                onClick={() => onSelectPlan(plan.id)}
                className={`w-full rounded-full py-6 text-sm font-medium transition-all ${
                  plan.highlighted
                    ? 'bg-black text-white hover:bg-neutral-800'
                    : 'bg-white text-black hover:bg-neutral-200'
                }`}
                data-testid={`buy-btn-${plan.id}`}
              >
                Comenzar ahora
              </Button>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

// Testimonials Section
function TestimonialsSection() {
  const testimonials = [
    {
      quote: "AG Collective transformó nuestra presencia digital. En 3 meses triplicamos nuestros leads y el engagement en redes creció exponencialmente.",
      author: "Carlos Restrepo",
      role: "CEO, TechStart México",
      image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&q=80"
    },
    {
      quote: "El equipo entiende perfectamente el mercado local. Sus estrategias de publicidad nos generaron un retorno de 5x en el primer mes.",
      author: "María González",
      role: "Directora, Clínica Estética Elite",
      image: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=150&q=80"
    },
    {
      quote: "La automatización con IA que implementaron nos ahorra más de 20 horas semanales. Ahora podemos enfocarnos en lo que realmente importa.",
      author: "Andrés Mejía",
      role: "Fundador, EcoFit Gym",
      image: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=150&q=80"
    }
  ];

  return (
    <AnimatedSection className="py-24 md:py-32 bg-neutral-950">
      <div className="max-w-6xl mx-auto px-6">
        <motion.div variants={fadeIn} className="text-center mb-16">
          <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Testimonios</p>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight">
            Lo que dicen nuestros clientes
          </h2>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((testimonial, i) => (
            <motion.div
              key={i}
              variants={fadeIn}
              className="p-8 rounded-2xl bg-neutral-900/30 border border-white/5"
            >
              <p className="text-neutral-300 leading-relaxed mb-8">"{testimonial.quote}"</p>
              <div className="flex items-center gap-4">
                <img 
                  src={testimonial.image} 
                  alt={testimonial.author}
                  className="w-12 h-12 rounded-full object-cover"
                />
                <div>
                  <p className="font-medium text-white">{testimonial.author}</p>
                  <p className="text-sm text-neutral-500">{testimonial.role}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatedSection>
  );
}

// Lead Generation Section
function LeadGenSection() {
  const [formData, setFormData] = useState({
    business_name: '',
    contact_name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          business_name: formData.business_name,
          phone: formData.phone,
          industry: 'Diagnóstico Gratuito',
          email: formData.email,
          google_maps_link: formData.contact_name
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        toast.success('¡Gracias! Te contactaremos pronto para agendar tu diagnóstico.');
        setFormData({ business_name: '', contact_name: '', email: '', phone: '' });
      } else {
        toast.error('Hubo un error. Por favor intenta de nuevo.');
      }
    } catch (error) {
      toast.error('Error de conexión. Por favor intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatedSection id="contacto" className="py-24 md:py-32 bg-black">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <motion.div variants={fadeIn}>
            <p className="text-sm text-neutral-500 uppercase tracking-wider mb-4">Contacto</p>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium tracking-tight mb-6">
              Solicita tu página demo gratuita
            </h2>
            <p className="text-lg text-neutral-400 mb-8 leading-relaxed">
              Agenda una llamada de 30 minutos con nuestro equipo para conocer tu negocio y mostrarte cómo podemos crear tu presencia digital.
            </p>
            
            <div className="space-y-4">
              {[
                "Demo personalizada de tu página web",
                "Propuesta de diseño sin compromiso",
                "Estrategia digital para tu negocio"
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-white/10 flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                  <span className="text-neutral-400">{item}</span>
                </div>
              ))}
            </div>
          </motion.div>
          
          <motion.div variants={fadeIn}>
            <form onSubmit={handleSubmit} className="p-8 rounded-2xl bg-neutral-900/50 border border-white/5" data-testid="lead-form">
              <div className="space-y-5">
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Nombre</label>
                  <input
                    type="text"
                    value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                    required
                    placeholder="Tu nombre completo"
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-white/30 focus:outline-none transition-colors"
                    data-testid="input-name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Empresa</label>
                  <input
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
                    required
                    placeholder="Nombre de tu empresa"
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-white/30 focus:outline-none transition-colors"
                    data-testid="input-company"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    placeholder="tu@email.com"
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-white/30 focus:outline-none transition-colors"
                    data-testid="input-email"
                  />
                </div>
                
                <div>
                  <label className="block text-sm text-neutral-400 mb-2">WhatsApp</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    required
                    placeholder="+52 444 204 0974"
                    className="w-full bg-black/50 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-neutral-600 focus:border-white/30 focus:outline-none transition-colors"
                    data-testid="input-phone"
                  />
                </div>
                
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-white text-black hover:bg-neutral-200 rounded-full py-6 text-base font-medium transition-all mt-4"
                  data-testid="submit-btn"
                >
                  {loading ? 'Enviando...' : 'Quiero crecer mi negocio'}
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      </div>
    </AnimatedSection>
  );
}

// Footer
function Footer() {
  return (
    <footer className="py-16 bg-neutral-950 border-t border-white/5">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="md:col-span-2">
            <img src={LOGO_URL} alt="AG Collective" className="h-14 w-auto invert mb-4" />
            <p className="text-neutral-500 mb-6 max-w-sm">
              Agencia de marketing digital en México. Ayudamos a negocios a crecer con estrategias digitales modernas y automatización con IA.
            </p>
            <div className="flex gap-4">
              <a 
                href="https://www.instagram.com/agcollective.me" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors"
                data-testid="instagram-link"
              >
                <Instagram className="w-5 h-5 text-neutral-400" />
              </a>
            </div>
          </div>
          
          {/* Links */}
          <div>
            <p className="font-medium text-white mb-4">Servicios</p>
            <ul className="space-y-3 text-neutral-500">
              <li><a href="#servicios" className="hover:text-white transition-colors">Social Media</a></li>
              <li><a href="#servicios" className="hover:text-white transition-colors">Publicidad Digital</a></li>
              <li><a href="#servicios" className="hover:text-white transition-colors">Diseño Web</a></li>
              <li><a href="#servicios" className="hover:text-white transition-colors">Automatización IA</a></li>
            </ul>
          </div>
          
          {/* Contact */}
          <div>
            <p className="font-medium text-white mb-4">Contacto</p>
            <ul className="space-y-3 text-neutral-500">
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4" />
                <span>agcollective.me@gmail.com</span>
              </li>
              <li className="flex items-start gap-2">
                <MessageCircle className="w-4 h-4 mt-1" />
                <div>
                  <p>+52 444 204 0974</p>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>Ciudad de México, México</span>
              </li>
            </ul>
          </div>
        </div>
        
        {/* Bottom */}
        <div className="flex flex-col md:flex-row justify-between items-center pt-8 border-t border-white/5">
          <p className="text-sm text-neutral-600">
            © 2024 AG Collective. Todos los derechos reservados.
          </p>
          <div className="flex gap-6 text-sm text-neutral-600 mt-4 md:mt-0">
            <Link to="/privacy" className="hover:text-white transition-colors" data-testid="footer-privacy-link">Política de privacidad</Link>
            <Link to="/terms" className="hover:text-white transition-colors" data-testid="footer-terms-link">Términos de servicio</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}

// WhatsApp Floating Button
function WhatsAppButton() {
  const whatsappNumber = "524442040974";
  const whatsappMessage = encodeURIComponent("Hola, me interesa conocer más sobre sus servicios de diseño web");

  return (
    <a
      href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
      target="_blank"
      rel="noopener noreferrer"
      className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all"
      data-testid="whatsapp-btn"
    >
      <MessageCircle className="w-6 h-6 text-white fill-white" />
    </a>
  );
}

// Main Landing Page Component
export default function LandingPage() {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const handleSelectPlan = (planId) => {
    setSelectedPlan(planId);
    setShowPaymentModal(true);
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />
      <HeroSection />
      <ProblemSection />
      <ServicesSection />
      <ResultsSection />
      <PortfolioSection />
      <ProcessSection />
      <PricingSection onSelectPlan={handleSelectPlan} />
      <TestimonialsSection />
      <LeadGenSection />
      <Footer />
      <WhatsAppButton />
      
      {showPaymentModal && (
        <PaymentModal 
          planId={selectedPlan} 
          onClose={() => setShowPaymentModal(false)} 
        />
      )}
    </div>
  );
}
